import React, { useState, useEffect, useRef, useMemo } from 'react';
import { produce } from 'immer';
import { Input, Select, Button, Col, Spin, Icon, message } from 'antd';
import { useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { isNumber } from 'src/utils/typeGuards';
import { objectKeys } from 'src/utils/objectKeys';
import { defaultErrorMessage } from 'src/constants/errorMessages';
import ProductAssociationLookup from '../types/ProductAssociationLookup';
import { ManufacturersDataItem, SourceOwnerDataItem } from '../api/productAssociation.api';
// TODO: [UI-2526] Request to hide options except SKUs
// import { getProductSegmentLookup, getManufacturersByIds } from '../services/productAssociation.service';
import { getProductSegmentLookup } from '../services/productAssociation.service';
import { LookupOptionsData, useLookupOptionsFetch } from './hooks/useLookupOptionsFetch';
import ConfirmationModal from 'src/components/common/ConfirmationModal';
import { FormModal } from '../FormModal';
import { Categories } from './LookupOptions/Categories';
import { CustomFilters } from './LookupOptions/CustomFilters';
import { Manufacturers } from './LookupOptions/Manufacturers';
import { Skus, MAX_LENGTH } from './LookupOptions/Skus';
import styles from './EditProductAssociationModal.module.less';

const { Option } = Select;

const SEPARATOR = /\s+/;

const CATEGORIES = 'Categories';
const CUSTOM_FILTERS = 'Custom Filters';
const MANUFACTURES = 'Manufacturers';
const SKUS = 'SKUs';

const antIcon = <Icon type="loading" spin />;
// TODO: [UI-2526] Request to hide options except SKUs
// const initOptions = [CATEGORIES, CUSTOM_FILTERS, MANUFACTURES, SKUS] as const;
const initOptions = [SKUS] as const;

type ProductAssociationState = {
  options: string[];
  categories: string[];
  customFilters: string[];
  manufacturers: ManufacturersDataItem[];
  skus: string;
};

const initState: ProductAssociationState = {
  options: [],
  categories: [],
  customFilters: [],
  manufacturers: [],
  skus: '',
};

type EditProductAssociationModalProps = {
  isOpenModal: boolean;
  hideModal: () => void;
  productName: string | null;
  productSegmentId?: number;
  onSubmit: (data: ProductAssociationLookup) => Promise<string | null>;
  associatedProductCount?: number;
};

const getDefaultSourceSite = (sourceSites: LookupOptionsData['sourceSites']) => {
  const found = sourceSites.find((el) => el.isDefault);
  return found?.id;
};

export const EditProductAssociationModal: React.FC<EditProductAssociationModalProps> = ({
  isOpenModal,
  hideModal,
  productName,
  productSegmentId,
  onSubmit,
  associatedProductCount,
}) => {
  const history = useHistory();
  const [state, setState] = useState<ProductAssociationState>(initState);
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isConfirmationVisible, setIsConfirmationVisible] = useState(false);
  const [isConfirmationCloseVisible, setIsConfirmationCloseVisible] = useState(false);
  const { setCurrentSourceSite, setManufacturerName, lookupOptionsData } = useLookupOptionsFetch();
  const defaultSourceSite = useMemo(
    () => getDefaultSourceSite(lookupOptionsData.sourceSites),
    [lookupOptionsData.sourceSites]
  );

  const editableProductSegmentLookup = useRef<ProductAssociationState>(initState);
  const isLookupsUnchanged = state === editableProductSegmentLookup.current;

  const isAnyLookupAdded = objectKeys(state).some((key) => {
    const value = state[key];
    if (key === 'options') {
      return false;
    }
    return value.length > 0;
  });

  const mergeState = (fn: (draft: ProductAssociationState) => void) => {
    setState(produce(fn));
  };

  const handleOptionAdd = () => {
    mergeState((draft: ProductAssociationState) => {
      draft.options.push('');
    });
  };

  const handleOptionRemove = (i: number) => {
    mergeState((draft: ProductAssociationState) => {
      switch (draft.options[i]) {
        case CATEGORIES:
          draft.categories = initState.categories;
          break;
        case CUSTOM_FILTERS:
          draft.customFilters = initState.customFilters;
          break;
        case MANUFACTURES:
          draft.manufacturers = initState.manufacturers;
          break;
        case SKUS:
          draft.skus = initState.skus;
          break;
      }
      draft.options.splice(i, 1);
    });
  };

  const handleOptionTypeChange = (value: string, i: number) => {
    mergeState((draft: ProductAssociationState) => {
      draft.options[i] = value;
    });
  };

  const handleCategoriesChange = (value: string[]) => {
    mergeState((draft: ProductAssociationState) => {
      draft.categories = value;
    });
  };

  const handleCustomFiltersChange = (value: string[]) => {
    mergeState((draft: ProductAssociationState) => {
      draft.customFilters = value;
    });
  };

  const handleManufacturersChange = (value: ManufacturersDataItem) => {
    mergeState((draft: ProductAssociationState) => {
      const i = draft.manufacturers.findIndex(({ id }) => id === value.id);
      if (i === -1) {
        draft.manufacturers.push(value);
      } else {
        draft.manufacturers.splice(i, 1);
      }
    });
  };

  const handleSkusChange = (value: string) => {
    mergeState((draft: ProductAssociationState) => {
      draft.skus = value;
    });
  };

  const handleClearAll = () => {
    mergeState((draft: ProductAssociationState) => {
      draft.options = initState.options;
      draft.skus = initState.skus;
      draft.customFilters = initState.customFilters;
      draft.categories = initState.categories;
    });
  };

  const clearForm = () => {
    setIsConfirmationVisible(false);
    handleClearAll();
  };

  const handleSourceSiteChange = (val: string) => {
    handleClearAll();
    setCurrentSourceSite(val);
  };

  const prepareCustomFilters = (customFilters: string[]) => {
    const customFiltersData: Record<string, string[]> = {};

    customFilters.forEach((filter) => {
      const [key, value] = filter.split('\n');
      if (!customFiltersData[key]) {
        customFiltersData[key] = [];
      }
      if (value) {
        customFiltersData[key].push(value);
      }
    });

    return Object.entries(customFiltersData).map(([lookupKey, lookupValues]) => ({
      lookupKey,
      lookupValues,
    }));
  };

  const handleSubmit = async () => {
    const skus = [...new Set(state.skus.split(SEPARATOR).filter(Boolean))];
    skus.splice(MAX_LENGTH);

    const prepareData: ProductAssociationLookup = {
      productSegmentId: String(productSegmentId),
      sourceOwnerId: lookupOptionsData.currentSourceSite || '',
      lookupType: {
        categories: state.categories,
        manufacturers: state.manufacturers.map((el) => String(el.id)),
        skus: skus,
        customfilters: prepareCustomFilters(state.customFilters),
      },
    };
    const segmentId = await onSubmit(prepareData);
    return segmentId;
  };

  const handleConfigureAlerts = async () => {
    const createdSegmentId = await handleSubmit();
    const segmentId = productSegmentId ?? createdSegmentId;
    return history.push(`/productAlerts/alertManagement/${segmentId}/new`);
  };

  const handleHideModal = () => {
    handleClearAll();
    setIsConfirmationCloseVisible(false);
    hideModal();
  };

  const setProductSegmentLookup = async (productSegmentId: number, defaultSourceSite: string) => {
    setLoading(true);
    try {
      const { lookupType, sourceOwnerId } = await getProductSegmentLookup(productSegmentId);
      // TODO: [UI-2526] Request to hide options except SKUs

      // const manufacturers = lookupType.manufacturers.length
      //   ? await getManufacturersByIds(lookupType.manufacturers)
      //   : [];

      // const customFilters = lookupType.customfilters.flatMap(({ lookupKey, lookupValues }) => {
      //   return lookupKey ? lookupValues.map((value) => [lookupKey, value].join('\n')) : [];
      // });

      if (sourceOwnerId) {
        setCurrentSourceSite(String(sourceOwnerId));
      } else {
        setCurrentSourceSite(defaultSourceSite);
      }

      setState((state) => {
        const newState = produce(state, (draft: ProductAssociationState) => {
          // TODO: [UI-2526] Request to hide options except SKUs
          // if (manufacturers.length) {
          //   draft.options.push(MANUFACTURES);
          //   draft.manufacturers = manufacturers;
          // }
          // if (lookupType.categories.length) {
          //   draft.options.push(CATEGORIES);
          //   draft.categories = lookupType.categories;
          // }
          // if (customFilters.length) {
          //   draft.options.push(CUSTOM_FILTERS);
          //   draft.customFilters = customFilters;
          // }
          if (lookupType.skus.length) {
            draft.options.push(SKUS);
            draft.skus = lookupType.skus.join('\n');
          }
        });
        return (editableProductSegmentLookup.current = newState);
      });
    } catch (e) {
      message.error(defaultErrorMessage);
      console.error(e);
    }
    setLoading(false);
  };

  useEffect(() => {
    // TODO we need to separate <EditProductAssociationModal /> onto several components
    // because currently the logic for creating and editing is completely mixed and
    // we have to use such checks to distinguish between them in lots of places.
    if (defaultSourceSite) {
      if (productSegmentId) {
        // editing mode
        setProductSegmentLookup(productSegmentId, defaultSourceSite);
      } else {
        setCurrentSourceSite(defaultSourceSite);
      }
    }
  }, [defaultSourceSite]);

  const removedOptions = new Set<string>();
  if (lookupOptionsData.customFiltersData.length === 0) {
    removedOptions.add(CUSTOM_FILTERS);
  }
  if (lookupOptionsData.categoriesData.length === 0) {
    removedOptions.add(CATEGORIES);
  }

  const availableOptions = initOptions.filter((el) => !removedOptions.has(el));
  const notSelectedOptions = availableOptions.filter((el) => !state.options.includes(el));

  return (
    <FormModal
      isModalOpen={isOpenModal}
      hideModal={() => (!isLookupsUnchanged ? setIsConfirmationCloseVisible(true) : hideModal())}
    >
      {productSegmentId && !isEditMode && <div className={styles.overlay}></div>}
      <h1>Product Association</h1>
      <div className={styles.content}>
        <div className={styles.block}>
          <label>Product Segment Name:</label>
          <Col span={18}>
            <Input disabled={true} name="productName" value={productName || ''} />
          </Col>
          {productSegmentId && !isEditMode && (
            <Col span={4}>
              <Button
                className={styles.editPermissionBtn}
                type="primary"
                size="small"
                onClick={() => setIsEditMode(true)}
              >
                Edit
              </Button>
            </Col>
          )}
        </div>

        <div className={styles.block}>
          <label>Source Site:</label>
          {lookupOptionsData.sourceSites.length > 0 && (
            <Select
              className={styles.fullWidth}
              value={lookupOptionsData.currentSourceSite ?? defaultSourceSite}
              onChange={handleSourceSiteChange}
            >
              {lookupOptionsData.sourceSites.map((item: SourceOwnerDataItem) => (
                <Option key={item.id} value={item.id}>
                  {item.name}
                </Option>
              ))}
            </Select>
          )}
        </div>

        {productSegmentId && loading && <Spin className={styles.spinner} indicator={antIcon} />}

        {state.options.map((key, i) => {
          return (
            <div className={styles.block} key={i}>
              <label>{i === 0 ? 'Product Lookup:' : 'Additional Product Lookup:'}</label>
              <div className={styles.xRow}>
                <Select
                  className={styles.fullWidth}
                  placeholder="Select an option"
                  value={key}
                  onChange={(value) => handleOptionTypeChange(value, i)}
                >
                  {notSelectedOptions.map((el) => (
                    <Option key={el} value={el}>
                      {el}
                    </Option>
                  ))}
                </Select>
                {state.options.length > 1 && (
                  <FontAwesomeIcon
                    icon={['fal', 'trash-alt']}
                    className={styles.deleteOptionBtn}
                    onClick={() => handleOptionRemove(i)}
                  />
                )}
              </div>

              {key === CATEGORIES && (
                <Categories
                  data={lookupOptionsData.categoriesData}
                  currentValue={state.categories}
                  onChange={handleCategoriesChange}
                  currentSourceSite={lookupOptionsData.currentSourceSite}
                />
              )}
              {key === CUSTOM_FILTERS && (
                <CustomFilters
                  data={lookupOptionsData.customFiltersData}
                  currentValue={state.customFilters}
                  onChange={handleCustomFiltersChange}
                />
              )}
              {key === MANUFACTURES && (
                <Manufacturers
                  data={lookupOptionsData.manufacturersData}
                  onChange={handleManufacturersChange}
                  setManufacturerName={setManufacturerName}
                  currentValue={state.manufacturers}
                  loading={lookupOptionsData.loading}
                />
              )}
              {key === SKUS && <Skus onChange={handleSkusChange} currentValue={state.skus} />}
            </div>
          );
        })}

        {state.options.length < availableOptions.length && (
          <Button type="dashed" onClick={handleOptionAdd} className={styles.addOptionBtn}>
            <FontAwesomeIcon icon={['fal', 'plus']} /> Add Product Lookup
          </Button>
        )}
        <div className={styles.footerButtons}>
          <Button
            type="primary"
            htmlType="submit"
            onClick={handleSubmit}
            disabled={(!productSegmentId && !isAnyLookupAdded) || isLookupsUnchanged}
          >
            {productSegmentId ? 'Save' : 'Submit'}
          </Button>
          <Button
            type="default"
            disabled={(!productSegmentId && !isAnyLookupAdded) || isLookupsUnchanged}
            onClick={handleConfigureAlerts}
            className={styles.btnConfigureAlert}
          >
            Configure Alerts
          </Button>
        </div>
        {!!isAnyLookupAdded && (
          <div className={styles.resetBtn} onClick={() => setIsConfirmationVisible(true)}>
            <FontAwesomeIcon icon={['fal', 'trash-alt']} />
            Clear All
          </div>
        )}
      </div>
      {isNumber(associatedProductCount) && (
        <div className={styles.productsCount}>
          <h3>Total number of the associated products: {associatedProductCount}</h3>
        </div>
      )}
      <ConfirmationModal
        visible={isConfirmationVisible}
        confirmText="Yes"
        onClose={() => setIsConfirmationVisible(false)}
        onConfirm={clearForm}
      >
        <h2>Are you sure you want to clear form?</h2>
      </ConfirmationModal>
      <ConfirmationModal
        visible={isConfirmationCloseVisible}
        confirmText="Yes"
        onClose={() => setIsConfirmationCloseVisible(false)}
        onConfirm={handleHideModal}
      >
        <h2>
          Are you sure you want to close form? <br />
          Your unsaved data will be lost.
        </h2>
      </ConfirmationModal>
    </FormModal>
  );
};
